import { NgClass, NgIf } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import {
  Component,
  ElementRef,
  EventEmitter,
  inject,
  OnDestroy,
  OnInit,
  Output,
  Renderer2,
  ViewChild,
} from '@angular/core';
import {
  FormBuilder,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { Router, RouterModule } from '@angular/router';
import { Capacitor } from '@capacitor/core';
import { TranslocoDirective, TranslocoService } from '@jsverse/transloco';
import { MessageService } from 'primeng/api';
import { ButtonModule } from 'primeng/button';
import { CheckboxModule } from 'primeng/checkbox';
import { FloatLabelModule } from 'primeng/floatlabel';
import { PasswordModule } from 'primeng/password';
import { Subject, takeUntil } from 'rxjs';
import { EMAIL_REGEX } from '../../../constants';
import { AuthService } from '../../../services/auth/auth.service';
import { DialogService } from '../../../services/dialog-service.service';
import { AppleLoginComponent } from './apple-login/apple-login.component';
import { GoogleLoginComponent } from './google-login/google-login.component';

@Component({
  selector: 'app-login-signup',
  standalone: true,
  imports: [
    FormsModule,
    ReactiveFormsModule,
    ButtonModule,
    NgClass,
    TranslocoDirective,
    AppleLoginComponent,
    GoogleLoginComponent,
    CheckboxModule,
    PasswordModule,
    FloatLabelModule,
    RouterModule,
    NgIf,
  ],
  templateUrl: './login-signup.component.html',
  styleUrl: './login-signup.component.scss',
})
export class LoginSignupComponent implements OnInit, OnDestroy {
  renderer = inject(Renderer2);
  fb = inject(FormBuilder);
  authService = inject(AuthService);
  messageService = inject(MessageService);
  translocoService = inject(TranslocoService);
  dialogService = inject(DialogService);
  isLoginDisabled$: boolean = true;
  isSignInDisabled$: boolean = true;
  isSignupMode = false;
  loginFormR: FormGroup;
  signupFormR: FormGroup;
  platform;
  @Output() loginSuccess = new EventEmitter<void>();
  @ViewChild('loginForm', { static: false }) loginForm!: ElementRef;
  @ViewChild('loginText', { static: false }) loginText!: ElementRef;
  @ViewChild('loginBtn', { static: false }) loginBtn!: ElementRef;
  @ViewChild('loginCheck', { static: false }) loginCheck!: ElementRef;
  @ViewChild('signupBtn', { static: false }) signupBtn!: ElementRef;
  messageDialog: any = {
    login: {
      summary: '',
      ok: { details: '' },
      error: { details: '' },
    },
    serverError: {
      summary: '',
      ok: { details: '' },
      error: { details: '' },
    },
  };
  private readonly destroy$ = new Subject<void>();
  http = inject(HttpClient);
  router = inject(Router);
  passwordVisible = false;
  password = '';

  constructor() {
    this.platform = Capacitor.getPlatform();
    this.loginFormR = this.fb.group({
      username: ['', [Validators.pattern(EMAIL_REGEX), Validators.required]],
      password: ['', [Validators.required, Validators.minLength(6)]],
    });
    this.signupFormR = this.fb.group(
      {
        emailAddress: ['', [Validators.required, Validators.pattern(EMAIL_REGEX)]],
        password: ['', [Validators.required]],
        confirmPassword: ['', Validators.required],
        privacyAccepted: [false, Validators.requiredTrue],
      },
      { validator: this.passwordMatchValidator },
    );
  }

  passwordMatchValidator(form: FormGroup) {
    return form.get('password')?.value === form.get('confirmPassword')?.value
      ? null
      : { mismatch: true };
  }

  ngOnInit(): void {
    this.loadTranslations();
    this.loginFormR.statusChanges.subscribe(() => {
      this.isLoginDisabled$ = this.loginFormR.invalid;
    });
    this.signupFormR.statusChanges.subscribe(() => {
      this.isSignInDisabled$ = this.signupFormR.invalid;
    });
  }

  submitLogin() {
    if (this.loginFormR.valid) {
      this.authService.login(this.loginFormR.value).subscribe({
        next: (response) => {
          this.loginSuccess.emit();
          this.messageService.add({
            severity: 'success',
            summary: this.messageDialog.login.summary,
            detail: this.messageDialog.login.ok.details,
          });
          this.authService.fetchUserFromServer().subscribe(() => {
            this.router.navigate(['dashboard']);
          });
        },
        error: (err) => {
          this.loginFormR.reset();
          if (!err?.networkError) {
            this.messageService.add({
              severity: 'error',
              summary: this.messageDialog.login.summary,
              detail: this.messageDialog.login.error.details,
            });
          } else {
            // this.messageService.add({
            //   severity: 'error',
            //   summary: this.messageDialog.serverError.summary,
            //   detail: this.messageDialog.serverError.error.details,
            // });
          }
        },
      });
    }
  }
  private loadTranslations(): void {
    this.translocoService
      .selectTranslateObject('message')
      .pipe(takeUntil(this.destroy$))
      .subscribe((translations) => {
        this.messageDialog.login.summary = translations.login.summary;
        this.messageDialog.login.error.details = translations.login.error.details;
        this.messageDialog.login.ok.details = translations.login.ok.details;
        this.messageDialog.login.ok.details = translations.login.ok.details;
        this.messageDialog.serverError.summary = translations.serverError.summary;
        this.messageDialog.serverError.error.details = translations.serverError.error.details;
      });
  }

  moveToSignup() {
    this.isSignupMode = true;
    this.renderer.setStyle(this.loginForm.nativeElement, 'marginLeft', '-100%');
    this.renderer.setStyle(this.loginText.nativeElement, 'marginLeft', '-50%');
  }

  moveToLogin() {
    this.isSignupMode = false;
    this.renderer.setStyle(this.loginForm.nativeElement, 'marginLeft', '0%');
    this.renderer.setStyle(this.loginText.nativeElement, 'marginLeft', '0%');
  }

  submitSignup() {
    const formData = {
      emailAddress: this.signupFormR.get('emailAddress')?.value,
      password: this.signupFormR.get('password')?.value,
      customFields: {
        privacyAccepted: this.signupFormR.get('privacyAccepted')?.value,
      },
    };
    this.authService
      .signUp(formData)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (resp) => {
          if (resp) {
            this.loginSuccess.emit();

            const successSummary = this.translocoService.translate('registration.successSummary');
            const successDetail = this.translocoService.translate('registration.successDetail');

            this.messageService.add({
              severity: 'success',
              summary: successSummary,
              detail: successDetail,
              life: 3000,
            });
          }
        },
        error: (error) => {
          const errorObj = JSON.parse(error.message);
          let errorSummary = this.translocoService.translate('registration.errorSummary');
          let errorDetail = this.translocoService.translate('registration.errorDetail');

          if (errorObj.registerCustomerAccountCustom.message === 'EMAIL_ADDRESS_CONFLICT_ERROR') {
            errorSummary = this.translocoService.translate(
              'registration.errorSummaryEmailAddressConflict',
            );
            errorDetail = this.translocoService.translate(
              'registration.errorDetailEmailAddressConflict',
            );
          }

          this.messageService.add({
            severity: 'error',
            summary: errorSummary,
            detail: errorDetail,
          });
        },
      });
  }

  togglePasswordVisibility() {
    this.passwordVisible = !this.passwordVisible; // Cambia lo stato della visibilità
  }

  goToPrivacy() {
    this.dialogService.hideDialog();
    this.router.navigate(['/privacy-policy']);
  }
  goToTerms() {
    this.dialogService.hideDialog();
    const url = this.translocoService.translate('routes.terms-and-conditions');
    this.router.navigate([url]);
  }
  goToRequestPassword() {
    this.dialogService.hideDialog();
    const url = this.translocoService.translate('routes.request-password');
    this.router.navigate([url]);
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
